import { LEADS_PAGE_SIZE } from '@/services/leads';
import { LeadSortDirection, LeadSortField } from '@/types/Leads';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { leadSortOptions } from './LeadManagementFilter';
import { LeadFilters } from './LeadManagementFilterPopover';

export function useLeadTabParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortId = searchParams.get('sort') ?? 'statusDateDesc';
  const appliedSort = leadSortOptions.find((o) => o.id === sortId) ?? leadSortOptions[leadSortOptions.length - 1];
  const sortField = appliedSort.id.replace(/(?:Asc|Desc)$/, '') as LeadSortField;
  const sortDirection: LeadSortDirection = appliedSort.id.endsWith('Asc') ? 'asc' : 'desc';
  const page = Number(searchParams.get('page')) || 1;
  const searchTerm = searchParams.get('search') ?? '';

  const states = useMemo(() => searchParams.get('states')?.split(',').filter(Boolean) ?? [], [searchParams]);
  const minBatteryKwh = searchParams.get('minBatteryKwh') ?? '';
  const maxBatteryKwh = searchParams.get('maxBatteryKwh') ?? '';
  const minSystemSize = searchParams.get('minSystemSize') ?? '';
  const maxSystemSize = searchParams.get('maxSystemSize') ?? '';
  const minPricePerWatt = searchParams.get('minPricePerWatt') ?? '';
  const maxPricePerWatt = searchParams.get('maxPricePerWatt') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';

  const parseNumericParam = (val: string): number | undefined => {
    if (!val) return undefined;
    const n = Number(val);
    return Number.isFinite(n) ? n : undefined;
  };

  const queryArgs = {
    sortField: sortField ?? undefined,
    sortDirection,
    search: searchTerm || undefined,
    pageNum: page,
    pageSize: LEADS_PAGE_SIZE,
    states: states.length > 0 ? states : undefined,
    minBatteryKwh: parseNumericParam(minBatteryKwh),
    maxBatteryKwh: parseNumericParam(maxBatteryKwh),
    minSystemSize: parseNumericParam(minSystemSize),
    maxSystemSize: parseNumericParam(maxSystemSize),
    minPricePerWatt: parseNumericParam(minPricePerWatt),
    maxPricePerWatt: parseNumericParam(maxPricePerWatt),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const activeFilterCount = [
    states.length > 0,
    minBatteryKwh !== '',
    maxBatteryKwh !== '',
    minSystemSize !== '',
    maxSystemSize !== '',
    minPricePerWatt !== '',
    maxPricePerWatt !== '',
    dateFrom !== '',
    dateTo !== '',
  ].filter(Boolean).length;

  const currentFilters: LeadFilters = useMemo(
    () => ({
      states,
      minBatteryKwh,
      maxBatteryKwh,
      minSystemSize,
      maxSystemSize,
      minPricePerWatt,
      maxPricePerWatt,
      dateFrom,
      dateTo,
    }),
    [
      states,
      minBatteryKwh,
      maxBatteryKwh,
      minSystemSize,
      maxSystemSize,
      minPricePerWatt,
      maxPricePerWatt,
      dateFrom,
      dateTo,
    ],
  );

  const handleSortSelection = useCallback(
    (sort: { id: string; label: string }) => {
      const next = new URLSearchParams(searchParams);
      next.set('sort', sort.id);
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', newPage.toString());
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleSearchChange = useCallback(
    (term: string) => {
      const next = new URLSearchParams(searchParams);
      if (term) {
        next.set('search', term);
      } else {
        next.delete('search');
      }
      next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleApplyFilters = useCallback(
    (filters: LeadFilters) => {
      const next = new URLSearchParams(searchParams);
      if (filters.states.length) next.set('states', filters.states.join(','));
      else next.delete('states');
      if (filters.minBatteryKwh) next.set('minBatteryKwh', filters.minBatteryKwh);
      else next.delete('minBatteryKwh');
      if (filters.maxBatteryKwh) next.set('maxBatteryKwh', filters.maxBatteryKwh);
      else next.delete('maxBatteryKwh');
      if (filters.minSystemSize) next.set('minSystemSize', filters.minSystemSize);
      else next.delete('minSystemSize');
      if (filters.maxSystemSize) next.set('maxSystemSize', filters.maxSystemSize);
      else next.delete('maxSystemSize');
      if (filters.minPricePerWatt) next.set('minPricePerWatt', filters.minPricePerWatt);
      else next.delete('minPricePerWatt');
      if (filters.maxPricePerWatt) next.set('maxPricePerWatt', filters.maxPricePerWatt);
      else next.delete('maxPricePerWatt');
      if (filters.dateFrom) next.set('dateFrom', filters.dateFrom);
      else next.delete('dateFrom');
      if (filters.dateTo) next.set('dateTo', filters.dateTo);
      else next.delete('dateTo');
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  return {
    queryArgs,
    page,
    searchTerm,
    appliedSort,
    activeFilterCount,
    currentFilters,
    handleSortSelection,
    handlePageChange,
    handleSearchChange,
    handleApplyFilters,
  };
}
